import { Form, InputRef } from 'antd';
import { NumberPadIntV2 } from 'components/NumberKeyBoard/Currency/NumberPadV2';
import { GiftCardNumberContainer } from 'features/cashier/components/CenterMainBody/ExtendFeatures/GiftCard/v2/styles';
import React, { forwardRef, useRef } from 'react';
import styled from 'styled-components';
import PercentInput from 'components/CurrencyInputPrecision/PercentInput';
import DollarAmountInput, { useDollarAmountInputRef } from 'components/DollarAmount/Input';
import DollarAmountKeyboard from 'components/DollarAmount/Keyboard';
import { DISCOUNT_VALUE_TYPE } from 'features/settingService/services/constants';
import { DiscountType } from 'features/cashier/components/Discount/DiscountMethodModal/DiscountModalV2/SalonDiscountTab';

export interface IAmountDiscountKeyboard {
  value?: number | null;
  onChange?: (value: IAmountDiscountKeyboard['value']) => void;
  inputClassName?: string;
  discountValueType: DISCOUNT_VALUE_TYPE;
  setDiscountValueType: (val: DISCOUNT_VALUE_TYPE) => void;
}

export type AmountDiscountKeyboardRef = {};

const AmountDiscountKeyboard = forwardRef<AmountDiscountKeyboardRef, IAmountDiscountKeyboard>(
  ({ value, onChange, discountValueType, setDiscountValueType }, ref) => {
    const inputDiscountValueRef = useRef<InputRef>(null);
    const dollarRef = useDollarAmountInputRef();
    const [form] = Form.useForm();
    const onTabChange = (tab: DISCOUNT_VALUE_TYPE) => {
      if (tab === DISCOUNT_VALUE_TYPE.DOLLAR) return setDiscountValueType(DISCOUNT_VALUE_TYPE.DOLLAR);
      setDiscountValueType(DISCOUNT_VALUE_TYPE.PERCENT);
    };

    return (
      <Container
        form={form}
        layout='vertical'
        initialValues={{
          discountType: DISCOUNT_VALUE_TYPE.PERCENT,
          discountValue: value || 0,
        }}
      >
        <ContainerDiscount>
          <ControlGroup style={{ marginTop: 0, flex: 1 }}>
            <div className='input-selector-container' style={{ height: 'unset' }}>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const tab = getFieldValue('discountType');
                  return (
                    <Form.Item
                      noStyle
                      name={'discountValue'}
                      rules={[
                        {
                          validator(_, value) {
                            if (!value) return Promise.reject('Please enter discount value');
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      {tab === DISCOUNT_VALUE_TYPE.PERCENT ? (
                        <PercentInput
                          height={64}
                          inputRef={inputDiscountValueRef}
                          inputMode='none'
                          className='gift-card-number-input fs-big fs-medium'
                          size='small'
                          onChangeDebounce={() => {
                            form.validateFields(['discountValue']);
                            const currentValue = form.getFieldValue('discountValue');
                            onChange?.(currentValue);
                          }}
                          maxValue={100}
                          onFocus={() => setDiscountValueType(DISCOUNT_VALUE_TYPE.PERCENT)}
                        />
                      ) : (
                        <DollarInputDiscountStyled
                          height={64}
                          ref={dollarRef}
                          inputMode='none'
                          className='gift-card-number-input fs-big fs-medium'
                          onChange={() => {
                            form.validateFields(['discountValue']);
                            const currentValue = form.getFieldValue('discountValue');
                            onChange?.(currentValue);
                          }}
                          onFocus={() => setDiscountValueType(DISCOUNT_VALUE_TYPE.DOLLAR)}
                        />
                      )}
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </div>
          </ControlGroup>
          <Form.Item noStyle name={'discountType'}>
            <DiscountType onChange={(value) => onTabChange(value as any)} />
          </Form.Item>
        </ContainerDiscount>
        <Form.Item hidden={discountValueType !== DISCOUNT_VALUE_TYPE.DOLLAR} noStyle name={'discountValue'}>
          <DollarAmountKeyboard
            controlRef={dollarRef}
            onChange={() => {
              form.validateFields(['discountValue']);
              const currentValue = form.getFieldValue('discountValue');
              onChange?.(currentValue);
            }}
          />
        </Form.Item>
        <Form.Item hidden={discountValueType !== DISCOUNT_VALUE_TYPE.PERCENT} noStyle name={'discountValue'}>
          <NumberPadIntV2
            controlRef={inputDiscountValueRef}
            max={100}
            onChange={() => {
              form.validateFields(['discountValue']);
              const currentValue = form.getFieldValue('discountValue');
              onChange?.(currentValue);
            }}
          />
        </Form.Item>
      </Container>
    );
  },
);

AmountDiscountKeyboard.displayName = 'AmountDiscountKeyboard';

export default AmountDiscountKeyboard;

const ContainerDiscount = styled.div`
  display: flex;
  align-self: stretch;
  align-items: center;
  margin: 0 1rem;
  margin-bottom: 0rem;
  box-shadow:
    0px 2px 4px 0px rgba(44, 35, 66, 0.4),
    0px 4px 8px -3px rgba(45, 35, 66, 0.3),
    0px -3px 0px 0px #d6d6e7 inset;
  border: 1px solid rgba(44, 35, 66, 0.4);
  border-radius: 6px;
  overflow: hidden;
`;

const Container = styled(Form)`
  display: flex;
  flex-direction: column;
  padding-top: 32px;
  background: #fff;
  .NumberPadStyled {
    background: #f5f5f5;
  }
`;

const ControlGroup = styled(GiftCardNumberContainer)`
  .gift-card-number-input {
    background: transparent;
    &.fs-medium {
      font-size: 36px;
    }
  }
  margin-bottom: 0;
`;

const DollarInputDiscountStyled = styled(DollarAmountInput)`
  justify-content: center;
  flex: 1;
  &.custom-input-focus input,
  input {
    border: none !important;
    box-shadow: none !important;
    text-align: center;
    outline: none !important;
  }
  .fake-labels,
  input,
  .mock-cursor {
    color: #1d2129;
    text-align: center;
    font-family: Poppins;
    align-self: stretch;
    font-size: 36px;
    height: unset;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  .fake-labels {
    top: 50%;
    transform: translateY(-50%);
  }
`;
